import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  LayoutDashboard,
  Loader2,
  RefreshCw,
  ShieldAlert,
} from "lucide-react";
import { getControlTower, setComplianceContext } from "../../services/compliance-api";
import { useComplianceAuth } from "../../context/ComplianceAuthContext";
import type {
  ControlTowerCaseItem,
  ControlTowerLane,
  ControlTowerResponse,
} from "../../types/compliance";

const lanes: Array<{
  id: ControlTowerLane;
  title: string;
  description: string;
}> = [
  {
    id: "blocked",
    title: "Blocked",
    description: "High-risk, overdue, or missing required evidence.",
  },
  {
    id: "needs_review",
    title: "Needs Review",
    description: "Open actions or mismatches requiring operator review.",
  },
  {
    id: "missing_evidence",
    title: "Missing Evidence",
    description: "Required shipment documents or evidence are not complete.",
  },
  {
    id: "compliance_review",
    title: "Compliance Review",
    description: "Compliance risks require operator review.",
  },
  {
    id: "customs_review",
    title: "Customs Review",
    description: "Customs discrepancies or field conflicts require review.",
  },
  {
    id: "finance_review",
    title: "Finance Review",
    description: "Financial exposure or value checks require finance review.",
  },
  {
    id: "ready_for_approval",
    title: "Ready For Approval",
    description: "No blocking actions detected.",
  },
  {
    id: "closed",
    title: "Approved / Closed",
    description: "Completed case outcomes.",
  },
];

function formatDate(value?: string | null) {
  if (!value) return "Not scheduled";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function laneColor(lane: string) {
  if (lane === "blocked") return "border-red-200 bg-red-50";
  if (lane === "missing_evidence") return "border-orange-200 bg-orange-50";
  if (lane === "customs_review") return "border-purple-200 bg-purple-50";
  if (lane === "finance_review") return "border-cyan-200 bg-cyan-50";
  if (lane === "compliance_review") return "border-amber-200 bg-amber-50";
  if (lane === "needs_review") return "border-amber-200 bg-amber-50";
  if (lane === "ready_for_approval") return "border-emerald-200 bg-emerald-50";
  return "border-gray-200 bg-gray-50";
}

function CaseCard({ item }: { item: ControlTowerCaseItem }) {
  return (
    <Link
      to="/Compliance/logistics/cases"
      className="block rounded-lg border border-gray-200 bg-white p-3 shadow-sm transition hover:border-blue-300 hover:shadow"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-mono text-sm font-semibold text-gray-900">
            {item.case_id}
          </p>
          <p className="mt-1 truncate text-xs text-gray-600">
            {item.shipment_reference || item.batch_name}
          </p>
        </div>
        {item.overdue_actions > 0 ? (
          <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700">
            overdue
          </span>
        ) : item.ready_for_approval ? (
          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
            ready
          </span>
        ) : (
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-700">
            {item.status.replace(/_/g, " ")}
          </span>
        )}
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
        <div className="rounded-md bg-gray-50 px-2 py-1">
          <p className="font-semibold text-gray-900">{item.document_count}</p>
          <p className="text-gray-500">docs</p>
        </div>
        <div className="rounded-md bg-gray-50 px-2 py-1">
          <p className="font-semibold text-gray-900">{item.risk_count}</p>
          <p className="text-gray-500">risks</p>
        </div>
        <div className="rounded-md bg-gray-50 px-2 py-1">
          <p className="font-semibold text-gray-900">{item.open_actions}</p>
          <p className="text-gray-500">actions</p>
        </div>
      </div>
      <div className="mt-3 space-y-1 text-xs text-gray-500">
        <p>Next due: {formatDate(item.next_due_at)}</p>
        <p>Updated: {formatDate(item.updated_at)}</p>
        {item.high_priority_actions > 0 && (
          <p className="font-semibold text-red-700">
            {item.high_priority_actions} high-priority action{item.high_priority_actions === 1 ? "" : "s"}
          </p>
        )}
      </div>
    </Link>
  );
}

export default function ControlTower() {
  const { credentials } = useComplianceAuth();
  const tenantId = credentials?.tenantId ?? "default";
  const role = credentials?.role ?? "COMPLIANCE_ADMIN";
  const [data, setData] = useState<ControlTowerResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async (background = false) => {
    if (background) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const response = await getControlTower();
      setData(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Control Tower refresh failed");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    setComplianceContext(tenantId, role);
    void load();
    pollRef.current = setInterval(() => void load(true), 30_000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [tenantId, role, load]);

  const grouped = useMemo(() => {
    const map = new Map<string, ControlTowerCaseItem[]>();
    for (const lane of lanes) map.set(lane.id, []);
    for (const item of data?.items ?? []) {
      map.set(item.lane, [...(map.get(item.lane) ?? []), item]);
    }
    return map;
  }, [data]);

  if (loading && !data) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading Control Tower...</span>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-gray-50 p-6">
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <LayoutDashboard size={24} className="text-blue-700" />
            <h1 className="text-2xl font-bold text-gray-900">Shipment Control Tower</h1>
          </div>
          <p className="mt-1 text-sm text-gray-600">
            Live operations view for blocked, review, approval-ready, and closed shipment compliance cases.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {data && (
            <span className="text-xs text-gray-500">
              Generated {formatDate(data.generated_at)}
            </span>
          )}
          <button
            type="button"
            onClick={() => void load(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium text-white hover:bg-black"
          >
            <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase text-gray-500">Cases</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">
            {data?.summary.total_cases ?? 0}
          </p>
        </div>
        <div className="rounded-lg border border-red-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase text-gray-500">Blocked</p>
            <ShieldAlert size={16} className="text-red-600" />
          </div>
          <p className="mt-1 text-2xl font-bold text-red-700">
            {data?.summary.blocked_cases ?? 0}
          </p>
        </div>
        <div className="rounded-lg border border-amber-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase text-gray-500">Open Actions</p>
            <AlertTriangle size={16} className="text-amber-600" />
          </div>
          <p className="mt-1 text-2xl font-bold text-amber-700">
            {data?.summary.open_actions ?? 0}
          </p>
        </div>
        <div className="rounded-lg border border-red-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase text-gray-500">Overdue</p>
            <Clock size={16} className="text-red-600" />
          </div>
          <p className="mt-1 text-2xl font-bold text-red-700">
            {data?.summary.overdue_actions ?? 0}
          </p>
        </div>
        <div className="rounded-lg border border-emerald-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase text-gray-500">Ready</p>
            <CheckCircle2 size={16} className="text-emerald-600" />
          </div>
          <p className="mt-1 text-2xl font-bold text-emerald-700">
            {data?.summary.ready_for_approval_cases ?? 0}
          </p>
        </div>
      </div>

      {!data || data.items.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center shadow-sm">
          <p className="text-sm text-gray-600">
            No shipment cases yet. Start with Shipment Batch Analysis.
          </p>
          <Link
            to="/Compliance/logistics/documents"
            className="mt-4 inline-flex rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800"
          >
            Upload Batch
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
          {lanes.map((lane) => {
            const items = grouped.get(lane.id) ?? [];
            return (
              <section
                key={lane.id}
                className={`min-h-[360px] rounded-lg border p-3 ${laneColor(lane.id)}`}
              >
                <div className="mb-3">
                  <div className="flex items-center justify-between gap-2">
                    <h2 className="font-semibold text-gray-900">{lane.title}</h2>
                    <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-gray-700">
                      {items.length}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-gray-600">{lane.description}</p>
                </div>
                <div className="space-y-3">
                  {items.length === 0 ? (
                    <p className="rounded-lg border border-dashed border-gray-300 bg-white/70 p-4 text-center text-xs text-gray-500">
                      No cases in this lane.
                    </p>
                  ) : (
                    items.map((item) => <CaseCard key={item.case_id} item={item} />)
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
